import { useHookstate } from "@hookstate/core";
import ArrowDown from "../../assets/icons/ArrowDown";
import { MainUser, MainUserJsonSavedData } from "../../types";
import React, { useRef } from "react";
import { Avatar } from "../Avatar";
import { useDebounce, useOutsideClick } from "../../hooks/ui";
import { onInputChangeNative } from "../../misc/utils";
import {
  useMainOrganizationSearch,
  useMainOrganizations,
} from "../../hooks/main-data";

type UserSelectProps = {
  label: string;
  placeholder: string;
  fieldId: string;
  selectedUser: MainUserJsonSavedData;
  handleChange: (user: MainUserJsonSavedData) => void;
  editable?: boolean;
  source?: string;
};

export const UserSelect = ({
  label,
  placeholder,
  fieldId,
  selectedUser,
  handleChange,
  source = "external",
  editable = true,
}: UserSelectProps) => {
  const localState = useHookstate({});
  const state: Record<string, any> = localState.get();
  const searchValue = state.searchValue || "";
  const mode = state.mode || "view";
  const resetSearchResults = state.resetSearchResults || 0;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useOutsideClick(wrapperRef, () => localState.merge({ mode: "view" }), [
    "user-select-edit-parent",
  ]);
  const debouncedQuery = useDebounce(searchValue, 500);
  const organizations: any[] = useMainOrganizations();
  const [searchResults, searchLoading] = useMainOrganizationSearch(
    organizations[0]?.id,
    debouncedQuery,
    resetSearchResults,
    source,
  );

  const switchMode = (newMode: string) => () => {
    localState.merge({
      mode: newMode,
    });
    if (newMode === "edit") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const onUserSelect = (user: MainUserJsonSavedData) => () => {
    handleChange(user);
    localState.merge({
      searchValue: "",
      resetSearchResults: resetSearchResults + 1,
    });
    switchMode("view")();
  };

  const renderViewMode = () => {
    return (
      <div
        {...(editable ? { onClick: switchMode("edit") } : {})}
        className='bg-valley-background-blue px-4 py-3 flex flex-row items-center rounded-lg '
      >
        <div className='flex flex-row items-center flex-1'>
          {selectedUser.id ? (
            <React.Fragment>
              <Avatar user={{ json_saved_data: selectedUser } as MainUser} />
              <div className='ml-3 flex flex-col'>
                <span className='font-medium text-[15px] leading-[19.5px] text-valley-black'>
                  {selectedUser.lastname} {selectedUser.firstname}
                </span>
                <span className='mt-0.5 text-sm leading-[15.4px] text-valley-gray'>
                  {selectedUser.email}
                </span>
              </div>
            </React.Fragment>
          ) : (
            <span className='text-[15px] leading-[19.5px] text-valley-light-gray'>
              {placeholder}
            </span>
          )}
        </div>
        <ArrowDown additionalStyle='text-valley-light-gray' />
      </div>
    );
  };

  const renderEditMode = () => {
    return (
      <div className='relative flex flex-col user-select-edit-parent'>
        <input
          ref={inputRef}
          value={searchValue}
          onChange={onInputChangeNative(localState, "searchValue")}
          className='bg-valley-background-blue text-valley-black px-4 py-[19.5px] text-[15px] leading-[19.5px] rounded-lg'
          placeholder={placeholder}
        />
        <div className='top-[60px] absolute flex flex-col w-full bg-white shadow-lg tags-search-shadow py-2 rounded-xl z-10 max-h-60 overflow-y-auto'>
          {" "}
          {searchResults.map((r) => (
            <div
              key={r.id}
              className='bg-white p-3 cursor-pointer flex flex-row hover:bg-unix-background-blue'
              onClick={onUserSelect(r)}
            >
              <span className={`font-medium text-valley-gray`}>
                {r.lastname} {r.firstname} ({r.email})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className='user-select-wrapper' ref={wrapperRef}>
      <label
        htmlFor={fieldId}
        className='block text-sm font-semibold text-gray-700'
      >
        {label}
      </label>
      {mode === "view" ? renderViewMode() : renderEditMode()}
    </div>
  );
};
